import type { Floorplan } from '@vibesites/schema';
import { parseBedCount } from './bedroomOptions';

/** `'0'` = studio, `'1'`–`'5'` = bedroom count, `'townhome'` = name/code/description match. */
export type PlanType = '0' | '1' | '2' | '3' | '4' | '5' | 'townhome';

const TOWNHOME_PATTERN = /town\s*-?\s*(home|house)s?|\bTH\b/i;

function isTownhome(plan: Floorplan): boolean {
  return [plan.floorplan_name, plan.floorplan_code, plan.floorplan_description].some(
    (v) => typeof v === 'string' && TOWNHOME_PATTERN.test(v),
  );
}

export function matchesPlanType(plan: Floorplan, planType: PlanType | undefined): boolean {
  if (plan.floorplan_is_disabled) return false;
  if (!planType) return true;
  if (planType === 'townhome') return isTownhome(plan);
  return parseBedCount(plan.floorplan_beds_min) === Number(planType);
}

interface PageWithSections {
  sections?: { slot?: string; props?: Record<string, unknown> }[];
}

/** The `planType` of the page's FloorplanShowcase section, if it has one. */
export function floorplanPageType(page: PageWithSections): PlanType | undefined {
  const section = page.sections?.find((s) => s.slot === 'FloorplanShowcase');
  return section?.props?.planType as PlanType | undefined;
}

/**
 * False for a floor-plan-type page (Studios, 1 Bedroom, Townhomes, …) when the
 * property has no floor plans of that type. Pages without a typed
 * FloorplanShowcase section are always offered.
 */
export function isFloorplanPageOffered(
  page: PageWithSections,
  floorplans: Floorplan[] | null | undefined,
): boolean {
  const planType = floorplanPageType(page);
  if (!planType) return true;
  return (floorplans || []).some((plan) => matchesPlanType(plan, planType));
}
